import { brannHack, nazjatarHack, valeeraHack } from '@/components/tooltips/reputation/hacks';
import { Constants } from '@/data/constants';
import { wowthingData } from '@/shared/stores/data';
import { userState } from '@/user-home/state/user';
import findReputationTier from '@/utils/find-reputation-tier';
import type { StaticDataReputation } from '@/shared/stores/static/types';
import type {
    Character,
    CharacterReputationParagon,
    CharacterReputationReputation,
    ReputationTier,
} from '@/types';
import type { ManualDataReputationSet } from '@/types/data/manual';

interface GetRenownDataParameters {
    character?: Character;
    reputation: ManualDataReputationSet;
    reputationsIndex: number;
    reputationSetsIndex: number;
    slug: string;
}

class RenownData {
    public characterParagon: CharacterReputationParagon;
    public characterRep: CharacterReputationReputation;
    public cls: string;
    public dataRep: StaticDataReputation;
    public renownLevel: string;
    public renownCurrent: number;
    public renownMax: number;
    public repTier: ReputationTier;
}

export function getRenownData({
    character,
    reputation,
    reputationsIndex,
    reputationSetsIndex,
    slug,
}: GetRenownDataParameters): RenownData {
    const ret = new RenownData();

    if (!reputation) {
        return ret;
    }

    character ||= userState.general.characters[0];

    ret.dataRep = wowthingData.static.reputationById.get(
        reputation.both?.id || reputation.alliance?.id || reputation.horde?.id
    );

    ret.characterRep = ret.dataRep.accountWide
        ? { reputationId: ret.dataRep.id, value: userState.general.reputations[ret.dataRep.id] }
        : character.reputationData[slug].sets[reputationsIndex][reputationSetsIndex];

    const repValue = ret.characterRep.value || 0;
    if (ret.dataRep.renownCurrencyId > 0) {
        const currency = wowthingData.static.currencyById.get(ret.dataRep.renownCurrencyId);
        ret.renownMax = currency.maxTotal;

        ret.renownCurrent = repValue / (ret.dataRep.maxValues[0] || 2500);
        ret.cls = `quality${1 + Math.floor(ret.renownCurrent / (ret.renownMax / 4))}`;

        ret.characterParagon = ret.dataRep.accountWide
            ? userState.general.paragons[ret.characterRep.reputationId]
                  ?.asCharacterReputationParagon
            : character.paragons?.[ret.characterRep.reputationId];

        let tier = ret.renownCurrent;
        if (ret.characterParagon) {
            tier += ret.characterParagon.current / ret.characterParagon.max;
        }

        if (ret.dataRep.paragonId && ret.cls === 'quality5') {
            ret.cls = 'quality6';
        }

        ret.renownLevel = (Math.floor(tier * 100) / 100).toFixed(2);
    } else {
        const tiers =
            wowthingData.static.reputationTierById.get(ret.dataRep.tierId) ||
            wowthingData.static.reputationTierById.get(0);
        ret.repTier = findReputationTier(tiers, repValue);

        if (
            Constants.reputations.nazjatarFriends.includes(ret.dataRep.id) ||
            ret.dataRep.id === Constants.reputations.delveBrann ||
            ret.dataRep.id === Constants.reputations.delveValeera
        ) {
            const levelMatch = ret.repTier.name.match(/(\d{1,3})$/);
            if (levelMatch) {
                let oof: number;
                if (Constants.reputations.nazjatarFriends.includes(ret.dataRep.id)) {
                    oof = nazjatarHack(levelMatch[1]);
                } else if (ret.dataRep.id === Constants.reputations.delveBrann) {
                    oof = brannHack(levelMatch[1]);
                } else {
                    oof = valeeraHack(levelMatch[1]);
                }
                ret.cls = `reputation${oof} reputation${oof}-border`;

                // Brann hack to treat him as blocks of 10 levels
                let actualMax = 0;
                let actualValue = ret.repTier.value;
                const currentIndex = tiers.names.length - ret.repTier.tier;
                const start = Math.floor(currentIndex / 10) * 10;
                const end = Math.floor((start + 10) / 10) * 10;
                for (let i = start; i < end; i++) {
                    const diff = tiers.minValues[i] - (tiers.minValues[i - 1] || 0);
                    actualMax += diff;
                    if (i < currentIndex) {
                        actualValue += diff;
                    }
                }

                ret.renownLevel = ((actualValue / actualMax) * 100).toFixed(1) + '%';
            }
        } else {
            ret.cls = `reputation${ret.repTier.tier} reputation${ret.repTier.tier}-border`;
            ret.renownLevel = `${ret.repTier.percent}%`;

            if (ret.dataRep.paragonId) {
                ret.characterParagon = ret.dataRep.accountWide
                    ? userState.general.paragons[ret.characterRep.reputationId]
                          ?.asCharacterReputationParagon
                    : character.paragons?.[ret.dataRep.id];
                if (ret.repTier.tier === 1) {
                    ret.cls = 'reputation0 reputation0-border';
                }
            }
        }
    }

    return ret;
}
